import React, { useEffect, useState } from 'react';
import { Alert, Button, Drawer, DrawerHeader, Typography } from '@one-thd/sui-atomic-components';
import { Store, Locker } from '@one-thd/sui-icons';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { ALERT_MESSAGES } from './constants';

// Ported one-to-one from @thd-olt-component-react/active-orders'
// components/fulfillment-details-actions/PickupLockerQRCode.tsx (OOP-4059) — see
// FulfillmentDetailsDrawer.tsx's doc comment. One substitution: the source fetches via a shared
// axios client carrying this repo's own auth/tracing headers (XB3 trace id, TMX profile id,
// bearer token) that don't exist here — ported as a plain `fetch`, matching this repo's own REST
// convention (see useActiveOrderFilterOptionsQuery.ts), same request/response shape otherwise.
const OMS_LOCKER_CODE_PATH = (purchaserId: string) => `/oms/customer/order/v1/user/${purchaserId}/locker/code`;

async function fetchLockerCode(purchaserId: string, orderNumber: string, storeNumber: string): Promise<string> {
  const response = await fetch(OMS_LOCKER_CODE_PATH(purchaserId), {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: '*/*', 'Content-Type': 'application/json' },
    body: JSON.stringify({ lockerCodeRequest: { customerAccountId: purchaserId, orderNumber, storeNumber } })
  });

  if (!response.ok) {
    throw new Error(`Locker code request failed with status ${response.status}`);
  }

  const data = await response.json();
  return data?.lockerAccessCodes?.[0]?.accessCode ?? '';
}

export interface PickupLockerQRCodeProps {
  buttonVariant?: 'primary' | 'secondary' | 'tertiary' | 'text' | 'ghost';
  orderNumber: string;
  fulfillmentGroup: any;
}

export const PickupLockerQRCode: React.FC<PickupLockerQRCodeProps> = ({ buttonVariant, orderNumber, fulfillmentGroup }) => {
  const [open, setOpen] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [lockerCode, setLockerCode] = useState('');

  const svocID = useThdCustomer()?.svocID;

  const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleModalClick = (event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  useEffect(() => {
    let cancelled = false;
    fetchLockerCode(svocID ?? '', orderNumber, fulfillmentGroup?.storePickup?.store?.number ?? '')
      .then((accessCode) => {
        if (!cancelled) {
          setLockerCode(accessCode);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHasError(true);
          setLockerCode('');
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <div className="[&_a]:sui-!justify-center [&_a:hover]:sui-!no-underline [&_a]:sui-!w-full [&_button]:sui-!w-full">
        <Button variant={buttonVariant} onClick={onClick} data-testid="pickup-locker-qr-code-button">
          View Locker Pickup QR Code
        </Button>
      </div>

      <Drawer open={open} onClose={handleClose} role="dialog" aria-modal="true" aria-label="Pickup Locker QR Code">
        <div
          onClick={handleModalClick}
          role="button"
          tabIndex={0}
          onKeyDown={(ev: React.KeyboardEvent<HTMLDivElement>) => {
            if (ev.key === 'Enter' || ev.key === ' ') {
              handleModalClick(ev);
            }
          }}
          style={{ height: hasError ? '345px' : '485px', cursor: 'default' }}
        >
          <DrawerHeader onClose={handleClose} title="Pickup Locker QR Code" />
          <div>
            <div className="sui-flex sui-flex-col sui-m-auto">
              {hasError || lockerCode === '' ? (
                <div className="sui-mx-4 sui-mb-3">
                  <Alert status="error" size="large">
                    <Typography variant="body-sm">{ALERT_MESSAGES.PICKUP_LOCKER_QR_CODE_ERROR}</Typography>
                  </Alert>
                </div>
              ) : (
                <div className="sui-flex sui-flex-col sui-items-center sui-m-auto sui-mb-1">
                  <div className="sui-overflow-hidden">
                    <img
                      src={`https://storage.googleapis.com/hd-custcomm-prod-qr-static/${lockerCode}.png`}
                      alt="QR Code Pic"
                      style={{ transform: 'scale(1.35)' }}
                      width="230"
                      height="230"
                    />
                  </div>

                  <div className="sui-flex sui-gap-1 sui-mt-1">
                    <Typography variant="body-sm" color="subtle" weight="bold">
                      Locker Code:
                    </Typography>
                    <Typography variant="body-sm" color="subtle" weight="bold" style={{ letterSpacing: '4px' }}>
                      {lockerCode}
                    </Typography>
                  </div>
                </div>
              )}
              <div className="sui-flex sui-gap-2 sui-mb-2 sui-mx-4">
                <Store size="large" />
                <Typography variant="body-sm">Go to your selected Home Depot store pickup location</Typography>
              </div>
              <div className="sui-flex sui-gap-2 sui-mx-4">
                <Locker size="large" />
                <Typography variant="body-sm">Scan the QR code at the pickup lockers to retrieve your item(s).</Typography>
              </div>
            </div>
          </div>
        </div>
      </Drawer>
    </div>
  );
};
